import { useEffect, useRef, useState } from 'react';
import { erlaubteAnsichten, type MapData, type MapObject, type UmgebungSummary } from '@etown/shared';
import { navigate } from '../../hooks/useHashRoute';
import { AUSWAHL_ROUTE } from '../../lib/routen';
import { GameRuntime } from '../../game/GameRuntime';
import { createView, is3dAvailable } from '../../renderer/createView';
import { loadViewModeVorliebe, useViewStore } from '../../stores/viewStore';
import { api, type SessionUser } from '../../net/api';
import { createSocket, joinRoom, type AppSocket } from '../../net/socketClient';
import { playRingTone } from '../../lib/ringTone';
import { useChatStore } from '../../stores/chatStore';
import { useDmStore } from '../../stores/dmStore';
import { useInteractionStore } from '../../stores/interactionStore';
import { useMediaStore } from '../../stores/mediaStore';
import { useMissedRingsStore } from '../../stores/missedRingsStore';
import { useObjectImagesStore } from '../../stores/objectImagesStore';
import { boardTextures } from '../../lib/boardTextures';
import { usePlayersStore } from '../../stores/playersStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { localMedia } from '../../webrtc/localMedia';
import { RtcManager } from '../../webrtc/RtcManager';
import { CallStrip, CallStripChip } from '../av/CallPanel';
import { MissedRingCards } from '../av/MissedRingCard';
import { RingToast } from '../av/RingToast';
import { ScreenWindows } from '../av/ScreenWindows';
import { VideoBubbleLayer } from '../av/VideoBubbleLayer';
import { ChatOverlay } from '../chat/ChatOverlay';
import { HudBar } from '../hud/HudBar';
import { ZoneCallBanner } from '../hud/ZoneCallBanner';
import { InteractionHint } from '../objects/InteractionHint';
import { FirstPersonHint } from './FirstPersonHint';
import { Minimap } from './Minimap';
import { SitzHinweis } from './SitzHinweis';
import { TouchSteuerung } from './TouchSteuerung';
import { PerfOverlay, isPerfEnabled } from './PerfOverlay';
import { ObjectModalHost } from '../objects/ObjectModalHost';
import { PresenceSidebar } from '../presence/PresenceSidebar';
import { OfficeContext, type OfficeServices } from './OfficeContext';
import './office.css';

type Phase = 'connecting' | 'ready' | 'error';

/**
 * Herzstueck des Clients: baut Socket-Verbindung und Spiel-Runtime auf,
 * verdrahtet alle Server-Events mit Stores/Runtime/RTC und rendert die
 * UI-Overlays ueber dem Canvas.
 */
export function OfficeView({ user, umgebung }: { user: SessionUser; umgebung: UmgebungSummary }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [services, setServices] = useState<OfficeServices | null>(null);
  const [phase, setPhase] = useState<Phase>('connecting');
  const [errorMsg, setErrorMsg] = useState('');
  /** Aktuelle Karte — die Minimap zeichnet daraus. */
  const [mapData, setMapData] = useState<MapData | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const socket: AppSocket = createSocket();
    const rtc = new RtcManager();
    let runtime: GameRuntime | null = null;
    let disposed = false;
    let joining = false;

    /** Join + Zustand uebernehmen — laeuft initial und nach jedem Reconnect. */
    const joinAndSync = async (): Promise<void> => {
      if (joining || disposed) return;
      joining = true;
      try {
        // RTC ZUERST initialisieren: direkt nach dem Join kann der Server
        // bereits proximity:enter senden (Spawn-Naehe) — der Manager muss
        // dann schon empfangsbereit sein.
        const config = await api.config();
        rtc.init(socket, config.iceServers as RTCIceServer[]);
        const snapshot = await joinRoom(socket, umgebung.mapId);
        if (disposed) return;

        usePlayersStore.getState().setSelf(snapshot.selfId);
        usePlayersStore.getState().setRoster(snapshot.players);
        useChatStore.getState().setHistory(snapshot.recentChat, snapshot.recentChat.length > 0);
        // Eigene Einstellungen + Audio-Verarbeitung uebernehmen
        useSettingsStore.getState().setSettings(snapshot.settings);
        localMedia.applyAudioSettings(snapshot.settings);
        // Aktuelle Bilder der 'picture'-Objekte
        useObjectImagesStore.getState().setAll(snapshot.objectImages);
        // Direktnachrichten-Konversationen laden
        useDmStore.getState().setSelf(user.id);
        socket.emit('dm:conversations', (res) => {
          if (res.ok) useDmStore.getState().setConversations(res.data.conversations);
        });
        // Wer waehrend der Abwesenheit angeklingelt hat
        socket.emit('ring:missed', (res) => {
          if (res.ok) useMissedRingsStore.getState().setAll(res.data.missed);
        });

        if (!runtime) {
          const self = snapshot.players.find((p) => p.id === snapshot.selfId);
          // Was die Karte anbietet, geht der Geraetevorliebe vor. Ohne
          // WebGL2 bleibt es still bei der Draufsicht — es sei denn, die
          // Karte gibt es nur von innen.
          const ansichten = erlaubteAnsichten(snapshot.map);
          const viewStore = useViewStore.getState();
          viewStore.setAnsichten(ansichten);
          if (ansichten === '3d' && !is3dAvailable()) {
            setErrorMsg('Diese Umgebung gibt es nur in der Ego-Perspektive – dein Browser unterstützt kein WebGL2.');
            setPhase('error');
            return;
          }
          const startMode = ansichten === '3d' ? '3d' : ansichten === '2d' ? '2d' : is3dAvailable() ? viewStore.mode : '2d';
          if (ansichten === 'beide') viewStore.setMode(startMode);
          else viewStore.setModeFluechtig(startMode);
          runtime = await GameRuntime.create({
            host,
            viewMode: startMode,
            createView,
            map: snapshot.map,
            selfId: snapshot.selfId,
            selfDisplayName: user.displayName,
            selfAvatarColor: self?.avatarColor ?? 0,
            selfAvatar: self?.avatar ?? user.avatar,
            selfRingRequired: snapshot.settings.ringRequired,
            initialPlayers: snapshot.players,
            sendMove: (move) => socket.emit('player:move', move),
            onInteract: (obj) => handleInteract(obj),
            onRing: (peerId) => {
              // Wie der Anklingel-Button (RingBubble): optimistisch outgoing + emit
              useMediaStore.getState().setPeerRing(peerId, 'outgoing');
              socket.emit('call:ring', { to: peerId }, (res) => {
                if (!res.ok) useMediaStore.getState().setPeerRing(peerId, 'none');
              });
            },
            getRingablePeerIds: () =>
              Object.values(useMediaStore.getState().peers)
                .filter((p) => !p.mediaActive && p.ring !== 'outgoing')
                .map((p) => p.peerId),
            onNearbyObject: (obj) => useInteractionStore.getState().setNearby(obj),
            onSitzen: (aktion) => useInteractionStore.getState().setSitzAktion(aktion),
            onZoneChange: (zone) => useInteractionStore.getState().setZone(zone),
            onZoneOccupancy: (occupancy) => {
              const map: Record<string, string[]> = {};
              for (const [zoneId, list] of occupancy) map[zoneId] = list.map((p) => p.displayName);
              useInteractionStore.getState().setZoneOccupants(map);
            },
            onPeerAudio: (peerId, cue) => rtc.setPeerAudio(peerId, cue),
            getConnectedPeerIds: () => rtc.connectedPeerIds(),
            getSpeakingPeerIds: () => rtc.speakingPeerIds(),
          });
          // Der Aufbau laedt nach (Renderer, Optik). Wurde die Ansicht
          // waehrenddessen verlassen, laeuft die fertige Runtime sonst mit
          // Bildschleife, Timer und eigenem WebGL-Kontext an einem
          // abgehaengten Host weiter.
          if (disposed) {
            runtime.destroy();
            runtime = null;
            return;
          }
          // Vorhandene Bilder in die Welt setzen
          for (const [objectId, imageId] of Object.entries(snapshot.objectImages)) {
            runtime.setObjectImage(objectId, imageId);
          }
          loadBoards(socket, runtime, snapshot.map);
          runtime.setDeniedZones(snapshot.deniedZoneIds);
          setMapData(snapshot.map);
          runtime.applyViewSettings(useViewStore.getState().settings);
          setServices({
            socket,
            runtime,
            rtc,
            mapId: snapshot.map.id,
            umgebung,
            istAdmin: user.isSuperadmin || umgebung.rolle === 'admin',
            mapTheme: snapshot.map.theme,
          });
          setPhase('ready');
        } else {
          // Reconnect: neue Socket-ID -> Roster/Runtime neu aufsetzen
          runtime.setMap(snapshot.map);
          setMapData(snapshot.map);
          for (const p of snapshot.players) runtime.addPlayer(p);
          for (const [objectId, imageId] of Object.entries(snapshot.objectImages)) {
            runtime.setObjectImage(objectId, imageId);
          }
          runtime.setDeniedZones(snapshot.deniedZoneIds);
        }
      } catch (err) {
        if (!disposed) {
          setErrorMsg(err instanceof Error ? err.message : 'Verbindung fehlgeschlagen');
          setPhase('error');
        }
      } finally {
        joining = false;
      }
    };

    const handleInteract = (obj: MapObject): void => {
      if (obj.type === 'link' && obj.props.url) {
        window.open(obj.props.url, '_blank', 'noopener,noreferrer');
        return;
      }
      useInteractionStore.getState().openInteraction(obj);
    };

    // --- Server-Events verdrahten ---
    socket.on('connect', () => void joinAndSync());
    socket.on('players:update', ({ moves }) => runtime?.applyServerUpdates(moves));
    socket.on('player:joined', (player) => {
      runtime?.addPlayer(player);
      usePlayersStore.getState().addPlayer(player);
    });
    socket.on('player:left', ({ id }) => {
      runtime?.removePlayer(id);
      usePlayersStore.getState().removePlayer(id);
      rtc.onProximityLeave(id);
    });
    socket.on('chat:message', (message) => useChatStore.getState().addMessage(message));
    socket.on('proximity:enter', (payload) => rtc.onProximityEnter(payload));
    socket.on('proximity:leave', ({ peerId }) => rtc.onProximityLeave(peerId));
    socket.on('webrtc:signal', ({ from, payload }) => rtc.handleSignal(from, payload));
    socket.on('webrtc:track-meta', ({ from, mid, kind }) => rtc.handleTrackMeta(from, mid, kind));

    // Avatar- und Einstellungs-Aenderungen anderer Spieler
    socket.on('player:avatar', ({ id, avatar }) => {
      runtime?.updateAvatar(id, avatar);
      usePlayersStore.getState().updateAvatar(id, avatar);
    });
    socket.on('player:updated', ({ id, ringRequired }) => {
      runtime?.setRingRequired(id, ringRequired);
      usePlayersStore.getState().setRingRequired(id, ringRequired);
    });

    // Anklingel-Fluss
    socket.on('call:ring', ({ peerId }) => {
      rtc.setRingState(peerId, 'incoming');
      playRingTone();
    });
    socket.on('call:accepted', ({ peerId }) => rtc.onCallAccepted(peerId));
    socket.on('call:declined', ({ peerId }) => rtc.setRingState(peerId, 'declined'));
    socket.on('call:cancelled', ({ peerId }) => {
      rtc.setRingState(peerId, 'none');
      useMediaStore.getState().setPeerRing(peerId, 'none');
    });
    socket.on('call:ended', ({ peerId }) => rtc.onCallEnded(peerId));
    // Der Anrufer hat aufgegeben: der Hinweis bleibt, bis er quittiert wird
    socket.on('ring:missed:new', (missed) => useMissedRingsStore.getState().addOrReplace(missed));

    // Direktnachrichten
    socket.on('dm:message', (message) => {
      const isActive = useDmStore.getState().addIncoming(message);
      if (isActive) socket.emit('dm:read', { peerUserId: message.fromUserId }, () => undefined);
    });

    // Bild eines 'picture'-Objekts gewechselt
    socket.on('object:image', ({ objectId, imageId }) => {
      useObjectImagesStore.getState().setImage(objectId, imageId);
      runtime?.setObjectImage(objectId, imageId);
    });

    // Whiteboard-Inhalt fuer die Flaeche in der Welt mitschreiben
    socket.on('whiteboard:stroke', ({ objectId, stroke }) => {
      boardTextures.apply(objectId, stroke);
      runtime?.setObjectBoard(objectId, boardTextures.canvasFor(objectId));
    });
    socket.on('whiteboard:cleared', ({ objectId }) => {
      boardTextures.clear(objectId);
      runtime?.setObjectBoard(objectId, boardTextures.canvasFor(objectId));
    });

    // Zutrittsrechte geaendert (Admin-Freigabe / restricted im Editor)
    socket.on('zone:access', ({ deniedZoneIds }) => runtime?.setDeniedZones(deniedZoneIds));

    socket.on('map:updated', async ({ mapId }) => {
      const { map } = await api.map(mapId);
      runtime?.setMap(map);
      setMapData(map);
      // Bilder nach dem Neuaufbau der Objekte erneut setzen
      for (const [objectId, imageId] of Object.entries(useObjectImagesStore.getState().images)) {
        runtime?.setObjectImage(objectId, imageId);
      }
      if (runtime) loadBoards(socket, runtime, map);
    });
    socket.on('connect_error', (err) => {
      if (err.message === 'UNAUTHORIZED') {
        setErrorMsg('Session abgelaufen — bitte neu anmelden');
        setPhase('error');
      }
    });

    socket.connect();

    return () => {
      disposed = true;
      socket.removeAllListeners();
      socket.disconnect();
      rtc.destroy();
      runtime?.destroy();
      localMedia.stopAll();
      usePlayersStore.getState().reset();
      useChatStore.getState().reset();
      useDmStore.getState().reset();
      useMissedRingsStore.getState().reset();
      useSettingsStore.getState().reset();
      useObjectImagesStore.getState().reset();
      boardTextures.reset();
      useInteractionStore.getState().setZoneOccupants({});
      useInteractionStore.getState().setSitzAktion(null);
      // Der erzwungene Modus gilt nur fuer diese Karte; die Vorliebe bleibt
      useViewStore.getState().setAnsichten('beide');
      useViewStore.getState().setModeFluechtig(loadViewModeVorliebe());
    };
    // user und umgebung wechseln nie waehrend einer Office-Sitzung (key in App)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Offenes Modal pausiert die Spielsteuerung
  const openObject = useInteractionStore((s) => s.openObject);
  useEffect(() => {
    services?.runtime.setInputBlocked(openObject !== null);
  }, [services, openObject]);

  return (
    <div className="office">
      {services && (
        <OfficeContext.Provider value={services}>
          <CallStrip />
        </OfficeContext.Provider>
      )}
      <div className="office__stage">
        <div ref={hostRef} className="office__canvas" />
        {phase === 'connecting' && <div className="office__status">Verbinde…</div>}
        {phase === 'error' && (
          <div className="office__status office__status--error">
            {errorMsg}
            <button onClick={() => window.location.reload()}>Neu laden</button>
            <button onClick={() => navigate(AUSWAHL_ROUTE)}>Zur Auswahl</button>
          </div>
        )}
        {services && (
          <OfficeContext.Provider value={services}>
            <div className="office__world-overlay" aria-hidden="true">
              <VideoBubbleLayer />
              <ScreenWindows />
              <InteractionHint />
            </div>
            <FirstPersonHint />
            <SitzHinweis />
            <TouchSteuerung />
            {mapData && <Minimap map={mapData} />}
            {isPerfEnabled() && <PerfOverlay />}
            <ZoneCallBanner />
            <CallStripChip />
            <div className="ring-stack">
              <RingToast />
              <MissedRingCards />
            </div>
            <PresenceSidebar />
            <ChatOverlay />
            <HudBar user={user} />
            <ObjectModalHost />
          </OfficeContext.Provider>
        )}
      </div>
    </div>
  );
}

/**
 * Whiteboard-Inhalte einmalig holen, damit sie in der Ego-Ansicht schon an der
 * Wand stehen, bevor jemand das Board oeffnet.
 */
function loadBoards(socket: AppSocket, runtime: GameRuntime, map: MapData): void {
  for (const obj of map.objects) {
    if (obj.type !== 'whiteboard') continue;
    socket.emit('whiteboard:load', { objectId: obj.id }, (res) => {
      if (!res.ok) return;
      boardTextures.clear(obj.id);
      for (const persisted of res.data.strokes) boardTextures.apply(obj.id, persisted.stroke);
      runtime.setObjectBoard(obj.id, boardTextures.canvasFor(obj.id));
    });
  }
}
